'use client'

import React, { useEffect, useState } from 'react'
import { Menu } from 'lucide-react'

interface Heading {
  id: string
  text: string
  level: number
}

interface TableOfContentsProps {
  content?: any
  headings?: Heading[]
}

export function TableOfContents({ content, headings: externalHeadings }: TableOfContentsProps) {
  const [headings, setHeadings] = useState<Heading[]>([])
  const [activeId, setActiveId] = useState<string>('')

  useEffect(() => {
    // If pre-extracted headings are provided (e.g. from block-based content), use them directly
    if (externalHeadings && externalHeadings.length > 0) {
      setHeadings(externalHeadings)
      return
    }

    // Otherwise extract headings from the Lexical rich text content
    const extractHeadings = (node: any, headingsList: Heading[] = []): Heading[] => {
      if (!node) return headingsList

      if (node.type === 'heading') {
        const text = node.children
          ?.map((child: any) => child.text || '')
          .join('')
          .trim()

        if (text) {
          const id = text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')

          // Only add heading if id is valid (non-empty)
          if (id) {
            headingsList.push({
              id,
              text,
              level: parseInt(node.tag?.replace('h', '') || '2'),
            })
          }
        }
      }

      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child: any) => extractHeadings(child, headingsList))
      }

      return headingsList
    }

    const extracted = extractHeadings(content?.root)
    setHeadings(extracted)
  }, [content, externalHeadings])

  useEffect(() => {
    // Add IDs to headings in the DOM
    headings.forEach((heading) => {
      const elements = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'))
      const element = elements.find((el) => {
        const text = el.textContent?.trim() || ''
        return text === heading.text
      })
      if (element && !element.id) {
        element.id = heading.id
      }
    })

    // Intersection Observer for active heading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
          }
        })
      },
      {
        rootMargin: '-100px 0px -66% 0px',
      },
    )

    headings.forEach((heading) => {
      // Skip if id is empty or invalid
      if (!heading.id) return

      const element = document.getElementById(heading.id)
      if (element) {
        observer.observe(element)
      }
    })

    return () => observer.disconnect()
  }, [headings])

  if (headings.length === 0) return null

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    // Guard against empty id
    if (!id) return

    const element = document.getElementById(id)
    if (element) {
      const offset = 100
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
      setActiveId(id)
    }
  }

  return (
    <>
      {/* Table of Contents */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-300">
          <Menu className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">On this page</h3>
        </div>

        <nav>
          <ul className="space-y-2">
            {headings.map((heading) => (
              <li key={heading.id} style={{ paddingLeft: `${(heading.level - 2) * 12}px` }}>
                <a
                  href={`#${heading.id}`}
                  onClick={(e) => handleClick(e, heading.id)}
                  className={`block text-sm transition-colors hover:text-brand-blue ${
                    activeId === heading.id
                      ? 'text-brand-blue font-medium border-l-2 border-brand-blue pl-3 -ml-3'
                      : 'text-gray-600'
                  }`}
                >
                  {heading.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </>
  )
}
